import type { OfflineAvatarTimelineAction } from '@/app/types';

export const MIN_TIMELINE_ACTION_DURATION = 0.5;
export const DEFAULT_TIMELINE_ACTION_DURATION = 5;
export const MAX_TIMELINE_ACTION_TEXT_LENGTH = 300;
export const MIN_TIMELINE_TEXT_LENGTH = 1;

export const isTimelineTextValid = (weightedLength: number, maxLength: number) =>
  weightedLength >= MIN_TIMELINE_TEXT_LENGTH && weightedLength <= maxLength;

type TimelineActionTimeField = 'start' | 'end';

const clampTimelineTime = (value: number, min: number, max: number) => {
  const boundedValue = Number.isFinite(value) ? value : min;
  const effectiveMax = Math.max(min, max);
  return Number(Math.min(Math.max(boundedValue, min), effectiveMax).toFixed(2));
};

export const createTimelineActionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `offline-avatar-action-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const normalizeTimelineActionRange = (start: number, end: number, duration: number) => {
  const maxDuration = Math.max(Number.isFinite(duration) ? duration : 0, 0);
  const normalizedStart = clampTimelineTime(start, 0, maxDuration);
  const normalizedEnd = clampTimelineTime(end, 0, maxDuration);

  return {
    start: normalizedStart,
    end: normalizedEnd,
  };
};

export const normalizeTimelineAction = (
  action: OfflineAvatarTimelineAction,
  duration: number,
): OfflineAvatarTimelineAction => {
  const range = normalizeTimelineActionRange(action.start, action.end, duration);

  return {
    ...action,
    text: action.text.slice(0, MAX_TIMELINE_ACTION_TEXT_LENGTH),
    ...range,
  };
};

export const createTimelineAction = (start: number, end: number, duration: number): OfflineAvatarTimelineAction => {
  const range = normalizeTimelineActionRange(start, end, duration);

  return {
    id: createTimelineActionId(),
    text: '',
    ...range,
  };
};

type TimelineActionInsertionRange = Pick<OfflineAvatarTimelineAction, 'start' | 'end'>;

const getBoundedTimelineTime = (value: number, maxDuration: number) =>
  Math.min(Math.max(Number.isFinite(value) ? value : 0, 0), maxDuration);

const getTimelineActionInsertionRange = (
  start: number,
  availableEnd: number,
  maxDuration: number,
): TimelineActionInsertionRange | null => {
  const boundedStart = getBoundedTimelineTime(start, maxDuration);
  const boundedEnd = getBoundedTimelineTime(availableEnd, maxDuration);

  if (boundedEnd - boundedStart < MIN_TIMELINE_ACTION_DURATION) return null;

  return {
    start: boundedStart,
    end: Math.min(boundedStart + DEFAULT_TIMELINE_ACTION_DURATION, boundedEnd),
  };
};

export const findTimelineActionInsertionRange = (
  actions: OfflineAvatarTimelineAction[],
  duration: number,
): TimelineActionInsertionRange | null => {
  const maxDuration = Math.max(duration, MIN_TIMELINE_ACTION_DURATION);
  const sortedActions = [...actions].sort((a, b) => a.start - b.start);

  // Keep the existing append behavior whenever there is room after the latest action.
  const latestEnd = Math.max(...sortedActions.map((action) => action.end), 0);
  const endRange = getTimelineActionInsertionRange(latestEnd, maxDuration, maxDuration);
  if (endRange) return endRange;

  if (sortedActions.length === 0) {
    return getTimelineActionInsertionRange(0, maxDuration, maxDuration);
  }

  // If the end is full, prefer the leading gap before the first action.
  const firstStart = getBoundedTimelineTime(sortedActions[0].start, maxDuration);
  const beginningRange = getTimelineActionInsertionRange(0, firstStart, maxDuration);
  if (beginningRange) return beginningRange;

  // Otherwise scan from the beginning and use the first gap between actions.
  let cursor = getBoundedTimelineTime(sortedActions[0].end, maxDuration);
  for (let index = 1; index < sortedActions.length; index += 1) {
    const action = sortedActions[index];
    const actionStart = getBoundedTimelineTime(action.start, maxDuration);
    const middleRange = getTimelineActionInsertionRange(cursor, actionStart, maxDuration);
    if (middleRange) return middleRange;

    cursor = Math.max(cursor, getBoundedTimelineTime(action.end, maxDuration));
  }

  return null;
};

export const appendTimelineAction = (
  actions: OfflineAvatarTimelineAction[],
  duration: number,
): OfflineAvatarTimelineAction[] => {
  const maxDuration = Math.max(duration, MIN_TIMELINE_ACTION_DURATION);
  const range = findTimelineActionInsertionRange(actions, maxDuration);

  if (!range) return actions;

  return [...actions, createTimelineAction(range.start, range.end, maxDuration)].sort((a, b) => a.start - b.start);
};

export const updateTimelineActionTime = (
  actions: OfflineAvatarTimelineAction[],
  id: string,
  field: TimelineActionTimeField,
  value: number,
  duration: number,
): OfflineAvatarTimelineAction[] => {
  if (!Number.isFinite(value)) return actions;

  const roundedValue = Math.round(value);
  const maxDuration = Math.max(Number.isFinite(duration) ? duration : 0, 0);
  const nextValue = clampTimelineTime(roundedValue, 0, maxDuration);

  return actions.map((action) =>
    action.id === id
      ? {
          ...action,
          [field]: nextValue,
        }
      : action,
  );
};

export const hasTimelineActionOverlap = (actions: OfflineAvatarTimelineAction[]): boolean => {
  const sortedActions = [...actions].sort((a, b) => a.start - b.start);

  return sortedActions.some((action, index) => index > 0 && action.start < sortedActions[index - 1].end);
};

export const hasTimelineActionOutOfBounds = (actions: OfflineAvatarTimelineAction[], duration: number): boolean => {
  const maxDuration = Math.max(Number.isFinite(duration) ? duration : 0, 0);

  return actions.some(
    (action) =>
      !Number.isFinite(action.start) ||
      !Number.isFinite(action.end) ||
      action.start < 0 ||
      action.end < 0 ||
      action.start > maxDuration ||
      action.end > maxDuration,
  );
};

export const removeTimelineAction = (actions: OfflineAvatarTimelineAction[], id: string) =>
  actions.filter((action) => action.id !== id);
